import type { AnswerValue, DecipherDatamap, RespondentRecord, WeightOf } from '../report-aggregation';

/**
 * A report's respondents as rows for a data download, before any provider's file shapes are put on
 * them. Every source holds its datamap in Decipher's shape whatever populated it -- uploads are
 * converted into it on the way in -- so this reads the same for every provider, and each provider's
 * exporter translates from here.
 *
 * Built on the source's raw datamap, not the deck's parsed layout, which leaves out open ends,
 * respondent metadata and lone numeric variables.
 */

export type DataDownloadFormat = 'csv' | 'xlsx' | 'sav';

export interface DataDownloadFile {
  file: Blob;
  fileName: string;
}

export interface DataExport {
  datamap: DecipherDatamap;
  /** In file order: the datamap's columns, any the data has that it doesn't list, then the weight. */
  columns: string[];
  records: RespondentRecord[];
  weight?: { column: string; of: WeightOf };
  /** Written as numbers in a spreadsheet. Text answers stay text, so `02134` keeps its leading zero. */
  numericColumns: Set<string>;
}

type DatamapVariable = DecipherDatamap['variables'][number];

/** A plain decimal, which a numeric column holds. Anything else in one is kept as text. */
export const DECIMAL = /^-?\d*\.?\d+$/;

/** The datamap calls the record number text; Decipher's own exports treat it as a number. */
export function isNumeric(variable: DatamapVariable): boolean {
  return variable.type === 'number' || variable.type === 'float' || variable.label === 'record';
}

export function dataExport(datamap: DecipherDatamap, records: RespondentRecord[], weightOf?: WeightOf): DataExport {
  const variables = datamap.variables ?? [];
  const listed = new Set(variables.map(variable => variable.label));
  const unlisted = new Set<string>();
  for (const record of records) {
    for (const column of Object.keys(record)) if (!listed.has(column)) unlisted.add(column);
  }

  const columns = [...listed, ...unlisted];
  const numericColumns = new Set(variables
    .filter(variable => variable.type === 'single' || variable.type === 'multiple' || isNumeric(variable))
    .map(variable => variable.label));

  if (!weightOf) return { datamap, columns, records, numericColumns };

  const column = weightColumnName(columns);
  numericColumns.add(column);
  return { datamap, columns: [...columns, column], records, weight: { column, of: weightOf }, numericColumns };
}

/**
 * `weight`, unless the data already has a column of that name. Compared without case, as SPSS
 * compares variable names.
 */
export function weightColumnName(columns: string[]): string {
  const taken = new Set(columns.map(column => column.toLowerCase()));
  if (!taken.has('weight')) return 'weight';
  let suffix = 1;
  while (taken.has(`weight_${suffix}`)) suffix++;
  return `weight_${suffix}`;
}

/** A cell as text: blank where the respondent wasn't shown the question, the weight to six places. */
export function cellText(data: DataExport, record: RespondentRecord, column: string): string {
  if (column === data.weight?.column) return data.weight.of(record).toFixed(6);
  const value: AnswerValue | AnswerValue[] | null | undefined = record[column];
  if (value === undefined || value === null) return '';
  return Array.isArray(value) ? value.join(',') : String(value);
}

/** A cell for a spreadsheet: empty, a number in a numeric column, otherwise text as it stands. */
export function cellValue(data: DataExport, record: RespondentRecord, column: string): string | number | null {
  const text = cellText(data, record, column);
  if (text === '') return null;
  return data.numericColumns.has(column) && DECIMAL.test(text) ? Number(text) : text;
}

/** The report's name made safe for a file name, as "Download the weights" makes it. */
export function fileBaseName(reportName: string): string {
  return reportName.replace(/[^\w\- .]+/g, '').trim() || 'report';
}
